import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, Typography, Alert, Space, Select, Row, Col, 
  Empty, Switch, Divider, Button, Tag, Popover, Form, Input
} from 'antd';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceArea
} from 'recharts';
import { Schedule } from '../../../model/schedule';
import { ScheduleOutlined } from '@ant-design/icons';
import { useAppContext } from '../../../libs/contextLib';
import EditScheduleView from '../../../editableList/schedules/EditScheduleView';
import { routeModelData, scheduleModelData } from '../../../services/ModelService';
import useAllRoutes from '../../../hooks/useAllRoutes';
import { Trip } from '../../../model/busRoute';
import { secsSinceMidnightToDayjs } from '../../../model/timeFilter';
import { toTime } from '../../../libs/formatLib';

const { Text, Paragraph } = Typography;

// Helper function to convert seconds to time string
const secondsToTime = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Custom Tooltip for better alignment and color
const tooltipLineColors = {
  scheduledTime: '#8884d8',
  proposedTime: '#52c41a', // OTR line
  normalTime: '#1890ff',
  empiricalTime: '#faad14',
  logNormalTime: '#d46b08',
  weibullTime: '#722ed1',
  robustTime: '#13c2c2',
  actualTime: '#999999',
};

const CustomTooltip = ({ active, label, payload }) => {
  if (!active || !payload || !payload.length) return null;
  // Filter out null/hidden values and actual times
  const filtered = payload.filter(
    item =>
      item.value !== null &&
      item.value !== undefined &&
      item.name !== 'Actual Time' &&
      !(typeof item.name === 'string' && item.name.startsWith('actualTime'))
  );
  // Get scheduledOTR from payload (if available)
  const scheduledOTR = payload[0]?.payload?.scheduledOTR;
  return (
    <div style={{ background: 'white', border: '1px solid #eee', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 500, marginBottom: 8 }}>Stop: {label}</div>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          {filtered.map((item, idx) => {
            // Format time as HH:MM
            let displayValue = item.value;
            if (typeof displayValue === 'number' && displayValue > 1000000) {
              const date = new Date(displayValue);
              const hours = date.getUTCHours().toString().padStart(2, '0');
              const minutes = date.getUTCMinutes().toString().padStart(2, '0');
              const seconds = date.getUTCSeconds().toString().padStart(2, '0');
              displayValue = `${hours}:${minutes}:${seconds}`;
            }
            // Use item.color if available, else fallback to our map using dataKey
            let color = item.stroke || item.color || tooltipLineColors[item.dataKey] || '#333';
            // Show OTR for scheduled time
            let name = item.name;
            if (item.dataKey === 'scheduledTime' && typeof scheduledOTR === 'number') {
              name = `Scheduled (${scheduledOTR.toFixed(1)}%)`;
            } else if (item.dataKey === 'proposedTime' && item.payload && typeof item.payload.proposedOTR === 'number') {
              name = `Best OTR (${item.payload.proposedOTR.toFixed(1)}%)`;
            }
            return (
              <tr key={item.name || idx}>
                <td style={{ color, fontWeight: 500, paddingRight: 12, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{name}</td>
                <td style={{ color, fontWeight: 400, textAlign: 'right', minWidth: 40 }}>{displayValue}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Add this function before the ProposedOTR component
const CustomXAxisTick = ({ x, y, payload, isTimingPoint }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill={isTimingPoint ? "#1890ff" : "#666"}
        transform="rotate(-45)"
        style={{ fontWeight: isTimingPoint ? 'bold' : 'normal' }}
      >
        {payload.value}
        {isTimingPoint && (
          <tspan dx={4} fill="#1890ff">●</tspan>
        )}
      </text>
    </g>
  );
};

const findTrip = (service, selectedTrip, selectedTripStart) => {
  if(service.tripId.includes(selectedTrip)) {
    console.log('Found trip:', service.tripId);
    return true;
  }
  if(secsSinceMidnightToDayjs(service.stopTimes[0].departSecs).format('HH:mm') === selectedTripStart) {
    console.log('Found trip:', service.tripId, selectedTripStart);
    return true;
  }
  return false;
}

const getRouteAndTrip = (allRoutesAsArray, selectedRoute, selectedTrip, selectedTripStart) => {
  if(!allRoutesAsArray) return;
  const routes = allRoutesAsArray.filter(route => route.routeNumber === selectedRoute);
  if(!routes.length) return;
  let route = routes.find(route => route.services.some(service => findTrip(service, selectedTrip, selectedTripStart)));
  if(!route) return;
  const trip = route.services.find(service => findTrip(service, selectedTrip, selectedTripStart));
  if(!trip || !trip.scheduleIds) return;
  return {route, trip};
}

/**
 * Proposed OTR Component
 * Displays actual stop times, scheduled times, and proposed optimal times for maximizing on-time performance
 * 
 * @param {Object} props - Component props
 * @param {Object} props.reportData - Pre-processed report data (fallback)
 * @param {string} props.selectedRoute - The selected route identifier
 * @param {string} props.selectedTrip - The selected trip identifier
 * @param {string} props.selectedTripStart - The trip start time
 * @param {Array} props.dateRange - Date range for analysis
 * @param {boolean} props.initialShowStopTimes - Whether to show actual stop times initially
 * @param {boolean} props.initialShowScheduledTime - Whether to show scheduled times initially
 * @param {boolean} props.initialShowProposedTime - Whether to show proposed times initially
 * @param {Array} props.filteredData - Pre-filtered data from parent component (optional, falls back to reportData)
 */
export const ProposedOTR = ({ 
  reportData,
  selectedRoute,
  selectedTrip,
  selectedTripStart,
  dateRange,
  initialShowStopTimes = true,
  initialShowScheduledTime = true,
  initialShowProposedTime = true,
  filteredData = null // Accept filtered data as prop
}) => {
  const {schedules} = useAppContext();
  const [showStopTimes, setShowStopTimes] = useState(initialShowStopTimes);
  const [showScheduledTime, setShowScheduledTime] = useState(initialShowScheduledTime);
  const [showProposedTime, setShowProposedTime] = useState(initialShowProposedTime);
  const schedulesAsArray = useMemo(() => schedules ? Object.values(schedules) : null, [schedules]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [showEditScheduleView, setShowEditScheduleView] = useState(false);
  const {allRoutesArray} = useAllRoutes();

  useEffect(() => {
    if(!allRoutesArray || !schedulesAsArray) return;
    const {trip} = getRouteAndTrip(allRoutesArray, selectedRoute, selectedTrip, selectedTripStart);
    if(!trip || !trip.scheduleIds) return;
    setSelectedSchedules(schedulesAsArray
      .filter(schedule => trip.scheduleIds.includes(schedule.scheduleId))
      .map(schedule => schedule.scheduleId));
  }, [allRoutesArray, selectedTrip, selectedRoute, schedulesAsArray]);

  // Process data for the selected trip
  const processData = useMemo(() => {
    // Use filtered data if provided, otherwise fall back to reportData
    let tripRecords = [];
    
    console.log('ProposedOTR processData - filteredData:', filteredData);
    console.log('ProposedOTR processData - reportData:', reportData);
    console.log('ProposedOTR processData - selectedTrip:', selectedTrip);
    
    if (filteredData && Array.isArray(filteredData)) {
      // Filter for just the current trip from the provided filtered data
      tripRecords = filteredData.filter(r => String(r.KEY || r.TRIP_ID) === String(selectedTrip));
      console.log('ProposedOTR processData - tripRecords from filteredData:', tripRecords.length);
    } else if (reportData && reportData.parsedData) {
      // Filter for selected route and trip from reportData
      tripRecords = reportData.parsedData.filter(stop => stop.tripId === selectedTrip);
      console.log('ProposedOTR processData - tripRecords from reportData:', tripRecords.length);
    } else {
      console.log('ProposedOTR processData - no data available');
      return null;
    }

    // Safety check for tripRecords
    if (!tripRecords || tripRecords.length === 0) {
      console.warn('ProposedOTR: No trip records found');
      return { chartData: [], tripDates: [] };
    }

    // Transform the data to match the expected format
    const transformedData = tripRecords.map(record => {
      // Handle both raw OTR data format and parsed report data format
      if (record.DATE_ID) {
        // Raw OTR data format - this is the current data structure
        const dateStr = String(record.DATE_ID);
        return {
          route: record.ROUTE || selectedRoute,
          tripId: record.TRIP_ID || selectedTrip,
          stopId: `${record.TSN}_${record.SEQUENCE || record.TIME_RECORD_SEQUENCE || '0'}`,
          stopName: record.STOP_NAME,
          scheduledTime: record.SCHEDULED_ARRIVAL_TIME ? 
            `${dateStr} ${toTime(record.SCHEDULED_ARRIVAL_TIME)}` : record.SCHEDULED_DEPARTURE_TIME ?
            `${dateStr} ${toTime(record.SCHEDULED_DEPARTURE_TIME)}` : null,
          actualTime: record.ACTUAL_ARRIVAL_TIME ? 
            `${dateStr} ${toTime(record.ACTUAL_ARRIVAL_TIME)}` : record.ACTUAL_DEPARTURE_TIME ?
            `${dateStr} ${toTime(record.ACTUAL_DEPARTURE_TIME)}` : null,
          varianceInSeconds: record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE,
          isTimingPoint: record.IS_TIMING_POINT === 'Y',
          tripDate: dateStr,
          TIME_RECORD_SEQUENCE: record.TIME_RECORD_SEQUENCE,
          stopSequence: record.SEQUENCE
        };
      } else if (record.tripId) {
        // Legacy parsed report data format (already in the right format)
        return {
          ...record,
          TIME_RECORD_SEQUENCE: record.TIME_RECORD_SEQUENCE || record.stopSequence,
          stopSequence: record.stopSequence || record.TIME_RECORD_SEQUENCE
        };
      } else {
        // Handle the current stopData format that's being passed in
        // This data comes from the OTR processor and has the raw field names
        const dateStr = String(record.DATE_ID);
        const scheduledTime = record.SCHEDULED_ARRIVAL_TIME ? 
          `${dateStr} ${toTime(record.SCHEDULED_ARRIVAL_TIME)}` : record.SCHEDULED_DEPARTURE_TIME ?
          `${dateStr} ${toTime(record.SCHEDULED_DEPARTURE_TIME)}` : null;
          
        const actualTime = record.ACTUAL_ARRIVAL_TIME ? 
          `${dateStr} ${toTime(record.ACTUAL_ARRIVAL_TIME)}` : record.ACTUAL_DEPARTURE_TIME ?
          `${dateStr} ${toTime(record.ACTUAL_DEPARTURE_TIME)}` : null;
        
        return {
          route: record.ROUTE || selectedRoute,
          tripId: record.TRIP_ID || selectedTrip,
          stopId: `${record.TSN}_${record.TIME_RECORD_SEQUENCE || record.SEQUENCE || '0'}`,
          stopName: record.STOP_NAME,
          scheduledTime,
          actualTime,
          varianceInSeconds: record.DEPARTURE_TIME_VARIANCE || record.ARRIVAL_TIME_VARIANCE,
          isTimingPoint: record.IS_TIMING_POINT === 'Y',
          tripDate: dateStr,
          TIME_RECORD_SEQUENCE: record.TIME_RECORD_SEQUENCE,
          stopSequence: record.SEQUENCE || record.TIME_RECORD_SEQUENCE
        };
      }
    });

    // Group by tripDate (each trip instance)
    const tripInstances = {};
    transformedData.forEach(stop => {
      const tripDate = stop.tripDate || 'Unknown Date';
      if (!tripInstances[tripDate]) tripInstances[tripDate] = [];
      tripInstances[tripDate].push(stop);
    });

    // For each trip instance, sort stops by TIME_RECORD_SEQUENCE (or scheduledTime) and exclude stops with actualTime of 0
    const chartLines = Object.entries(tripInstances).map(([tripDate, stops]) => {
      // Exclude stops where actualTime parses to 0 (i.e., '00:00:00' or missing/invalid)
      const filteredStops = stops.filter(stop => {
        const [h, m, s] = (stop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
        const actualMs = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        return actualMs !== 0;
      });
      const sortedStops = [...filteredStops].sort((a, b) => {
        // Prefer TIME_RECORD_SEQUENCE, fallback to scheduledTime
        const seqA = parseInt(a.TIME_RECORD_SEQUENCE || a.stopSequence || '0', 10);
        const seqB = parseInt(b.TIME_RECORD_SEQUENCE || b.stopSequence || '0', 10);
        if (!isNaN(seqA) && !isNaN(seqB) && seqA !== seqB) return seqA - seqB;
        // Fallback: sort by scheduledTime
        const [dateA, timeA] = (a.scheduledTime || '').split(' ');
        const [dateB, timeB] = (b.scheduledTime || '').split(' ');
        return (timeA || '').localeCompare(timeB || '');
      });
      return {
        tripDate,
        stops: sortedStops
      };
    });

    // Build chart data: one row per stop (by stopId), columns for each trip instance's actual time
    // Get all unique stopIds and sort them by TIME_RECORD_SEQUENCE
    const allStopIds = [];
    chartLines.forEach(line => {
      line.stops.forEach(stop => {
        if (!allStopIds.includes(stop.stopId)) allStopIds.push(stop.stopId);
      });
    });

    // Sort allStopIds by TIME_RECORD_SEQUENCE to ensure proper x-axis ordering
    allStopIds.sort((a, b) => {
      // Find the first occurrence of each stopId to get its sequence
      let seqA = 0, seqB = 0;
      for (const line of chartLines) {
        const stopA = line.stops.find(s => s.stopId === a);
        const stopB = line.stops.find(s => s.stopId === b);
        if (stopA && seqA === 0) {
          seqA = parseInt(stopA.TIME_RECORD_SEQUENCE || stopA.stopSequence || '0', 10);
        }
        if (stopB && seqB === 0) {
          seqB = parseInt(stopB.TIME_RECORD_SEQUENCE || stopB.stopSequence || '0', 10);
        }
        if (seqA !== 0 && seqB !== 0) break;
      }
      return seqA - seqB;
    });

    // Safety check - ensure we have stops to process
    if (allStopIds.length === 0) {
      console.warn('ProposedOTR: No stops found in trip data');
      return { chartData: [], tripDates: [] };
    }

    if(!schedulesAsArray) {
      // Don't return here - we can still show the chart without schedule functionality
    }

    // Build chartData: [{ stopName, stopId, scheduledTime, actual_<tripDate>: ms, ... }]
    const chartData = allStopIds.map(stopId => {
      // Use the first occurrence for stopName and scheduledTime
      let stopName = '';
      let scheduledTime = null;
      chartLines.forEach(line => {
        const found = line.stops.find(s => s.stopId === stopId);
        if (found && !stopName) stopName = found.stopName || `Stop ${stopId}`;
        if (found && scheduledTime === null) {
          const [h, m, s] = (found.scheduledTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          scheduledTime = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        }
      });
      
      const row = { stopName, stopId, scheduledTime };
      
      chartLines.forEach(line => {
        const found = line.stops.find(s => s.stopId === stopId);
        if (found) {
          const [h, m, s] = (found.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          const actualMs = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
          row[`actual_${line.tripDate}`] = actualMs;
        } else {
          row[`actual_${line.tripDate}`] = null;
        }
      });
      
      return row;
    });



    // Calculate average actual travel time between each pair of consecutive stops
    const avgActualTravelTimes = [];
    for (let i = 1; i < chartData.length; i++) {
      let travelTimes = [];
      chartLines.forEach(line => {
        const prevStop = line.stops.find(s => s.stopId === chartData[i - 1].stopId);
        const currStop = line.stops.find(s => s.stopId === chartData[i].stopId);
        if (prevStop && currStop) {
          // Parse actual times to ms
          const [h1, m1, s1] = (prevStop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          const [h2, m2, s2] = (currStop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
          const t1 = ((h1 || 0) * 60 * 60 + (m1 || 0) * 60 + (s1 || 0)) * 1000;
          const t2 = ((h2 || 0) * 60 * 60 + (m2 || 0) * 60 + (s2 || 0)) * 1000;
          if (t2 > t1) {
            travelTimes.push(t2 - t1);
          }
        }
      });
      avgActualTravelTimes[i] = travelTimes.length > 0 ? travelTimes.reduce((a, b) => a + b, 0) / travelTimes.length : 0;
    }

    // GLOBAL OPTIMIZATION: Dynamic programming for monotonic OTR maximization
    // 1. For each stop, build a sorted list of candidate times (actuals + scheduled time + every 30s from 2min before min to 2min after max)
    const candidateTimesPerStop = chartData.map((row, i) => {
      let times = chartLines.flatMap(line => {
        const stop = line.stops.find(s => s.stopId === row.stopId);
        if (!stop) return [];
        const [h, m, s] = (stop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
        const ms = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        return ms !== 0 ? [ms] : [];
      });
      // Always include scheduled time as a candidate
      if (row.scheduledTime && !times.includes(row.scheduledTime)) {
        times.push(row.scheduledTime);
      }
      // Expand to every 30s from 2min before min to 2min after max
      if (times.length > 0) {
        const min = Math.min(...times);
        const max = Math.max(...times);
        for (let t = min - 2 * 60 * 1000; t <= max + 2 * 60 * 1000; t += 30 * 1000) {
          times.push(t);
        }
      }
      // Sort and dedupe
      times = Array.from(new Set(times)).sort((a, b) => a - b);
      return times;
    });

    // Only proceed if all stops have enough candidates
    if (candidateTimesPerStop.every(times => times.length >= 5)) {
      // DP table: dp[i][j] = { sumOTR, prevIdx }
      // i = stop index, j = candidate index at stop i
      const dp = [];
      // Precompute OTRs for all candidates at all stops
      const otrs = candidateTimesPerStop.map((candidates, i) => {
        return candidates.map(candidate => {
          // Gather all actual arrival times for this stop (in ms)
          const actualTimes = chartLines.flatMap(line => {
            const stop = line.stops.find(s => s.stopId === chartData[i].stopId);
            if (!stop) return [];
            const [h, m, s] = (stop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
            const ms = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
            return ms !== 0 ? [ms] : [];
          });
          // Count how many actuals fall within [candidate-30s, candidate+120s]
          let count = 0;
          for (let k = 0; k < actualTimes.length; k++) {
            if (
              actualTimes[k] >= candidate - 30 * 1000 &&
              actualTimes[k] <= candidate + 120 * 1000
            ) {
              count++;
            }
          }
          return count / actualTimes.length * 100;
        });
      });
      
      // Safety check before DP initialization
      if (otrs.length === 0 || !otrs[0] || otrs[0].length === 0) {
        console.warn('ProposedOTR: No OTR data available for DP initialization');
        return { chartData: [], tripDates: [] };
      }
      
      // DP initialization for first stop
      dp[0] = otrs[0].map((otr, j) => ({ sumOTR: otr, prevIdx: null }));
      // DP for subsequent stops
      for (let i = 1; i < chartData.length; i++) {
        dp[i] = [];
        const minGap = 10 * 1000; // 10 seconds
        for (let j = 0; j < candidateTimesPerStop[i].length; j++) {
          let best = { sumOTR: -Infinity, prevIdx: null };
          for (let k = 0; k < candidateTimesPerStop[i - 1].length; k++) {
            if (candidateTimesPerStop[i][j] >= candidateTimesPerStop[i - 1][k] + minGap) {
              const sum = dp[i - 1][k].sumOTR + otrs[i][j];
              if (
                sum > best.sumOTR ||
                (sum === best.sumOTR && candidateTimesPerStop[i][j] < candidateTimesPerStop[i][best.prevIdx])
              ) {
                best = { sumOTR: sum, prevIdx: k };
              }
            }
          }
          dp[i][j] = best;
        }
      }
      // Find best final candidate (skip -Infinity)
      let bestFinal = { sumOTR: -Infinity, idx: null };
      for (let j = 0; j < dp[chartData.length - 1].length; j++) {
        if (dp[chartData.length - 1][j].sumOTR > bestFinal.sumOTR) {
          bestFinal = { sumOTR: dp[chartData.length - 1][j].sumOTR, idx: j };
        }
      }
      // Backtrack to recover optimal sequence, with guard for undefined prevIdx
      let sequence = Array(chartData.length).fill(null);
      let idx = bestFinal.idx;
      let lastValidIdx = idx;
      for (let i = chartData.length - 1; i >= 0; i--) {
        if (idx === null || idx === undefined || !dp[i][idx]) {
          // If we hit an undefined, fill the rest with the last valid index (or 0)
          for (let j = i; j >= 0; j--) {
            sequence[j] = lastValidIdx !== null && lastValidIdx !== undefined ? lastValidIdx : 0;
          }
          break;
        }
        sequence[i] = idx;
        lastValidIdx = idx;
        idx = dp[i][idx].prevIdx;
      }
      // Set proposedTime and proposedOTR for each stop
      for (let i = 0; i < chartData.length; i++) {
        const candidate = candidateTimesPerStop[i][sequence[i]];
        chartData[i].proposedTime = candidate;
        chartData[i].proposedOTR = otrs[i][sequence[i]];
      }
    }

    // Add timing point information to chartData
    chartData.forEach(row => {
      // Try to find timing point info from the transformed data first
      const stop = transformedData.find(item => 
        item.stopId === row.stopId
      );
      if (stop) {
        row.isTimingPoint = stop.isTimingPoint || false;
      } else if (reportData?.parsedData) {
        // Fallback to reportData if available
        const reportStop = reportData.parsedData.find(item => 
        item.stopId === row.stopId && 
        item.route === selectedRoute
      );
        row.isTimingPoint = reportStop?.isTimingPoint || false;
      } else {
        row.isTimingPoint = false;
      }
    });

    // Add scheduledOTR (current OTR for scheduled time) to each stop
    chartData.forEach(row => {
      // Gather all actual arrival times for this stop (in ms)
      const actualTimes = chartLines.flatMap(line => {
        const stop = line.stops.find(s => s.stopId === row.stopId);
        if (!stop) return [];
        const [h, m, s] = (stop.actualTime?.split(' ')[1] || '00:00:00').split(':').map(Number);
        const ms = ((h || 0) * 60 * 60 + (m || 0) * 60 + (s || 0)) * 1000;
        return ms !== 0 ? [ms] : [];
      });
      if (row.scheduledTime && actualTimes.length > 0) {
        const count = actualTimes.filter(t =>
          t >= row.scheduledTime - 30 * 1000 &&
          t <= row.scheduledTime + 120 * 1000
        ).length;
        row.scheduledOTR = (count / actualTimes.length) * 100;
      } else {
        row.scheduledOTR = null;
      }
    });

    // Validate chart data before returning
    const validChartData = chartData.filter(row => {
      const hasScheduledTime = row.scheduledTime !== null && row.scheduledTime !== undefined;
      const hasProposedTime = row.proposedTime !== null && row.proposedTime !== undefined;
      const hasActualTimes = Object.keys(row).some(key => key.startsWith('actual_') && row[key] !== null);
      return hasScheduledTime && hasProposedTime && hasActualTimes;
    });

    // Return both chartData and tripDates for legend
    return { chartData: validChartData, tripDates: chartLines.map(line => line.tripDate) };
  }, [reportData, selectedRoute, selectedTrip, dateRange, filteredData]);

  const chartData = processData ? processData.chartData : [];
  const tripDates = processData ? processData.tripDates : [];

  // Calculate average OTRs for display
  const avgScheduledOTR = chartData.length > 0 ? (chartData.reduce((sum, row) => sum + (row.scheduledOTR || 0), 0) / chartData.length) : null;
  const avgProposedOTR = chartData.length > 0 ? (chartData.reduce((sum, row) => sum + (row.proposedOTR || 0), 0) / chartData.length) : null;

  const handleCreateTimetable = (scheduleIds) => {
    const {route, trip} = getRouteAndTrip(allRoutesArray, selectedRoute, selectedTrip, selectedTripStart);
    console.log('Trip:', trip, chartData);
    if(!trip) return;
    const newTrip = new Trip({scheduleIds, stopTimes: [...trip.stopTimes]});
    trip.stopTimes.forEach((st, idx) => {
      const newSt = chartData[idx]
      if(newSt) {
        st.arriveSecs = newSt.proposedTime/1000;
        st.departSecs = newSt.proposedTime/1000;
      }
    });
    route.services.push(newTrip)
    route.services.sort((a, b) => a.stopTimes[0].departSecs - b.stopTimes[0].departSecs);
    routeModelData.save(route).then(() => console.log('Trip saved.'))
  };

  const schedulePopoverContent = (
    <div style={{ width: showEditScheduleView ? 1200 : 300 }}>
      <Form
        layout="vertical"
        onFinish={(values) => {
          console.log('Form submitted:', values);
          // TODO: Handle schedule update
        }}
      >
        {/* Only show the schedule dropdown if not editing */}
        {!showEditScheduleView && (
          <Form.Item
            label="Select Schedule"
            style={{ marginBottom: 16 }}
          >
            <Select
              placeholder="Choose a schedule"
              style={{ width: '100%' }}
              mode="multiple"
              options={schedulesAsArray?.map(schedule => ({
                label: schedule.scheduleName,
                value: schedule.scheduleId
              }))}
              value={selectedSchedules}
              onChange={(value) => {
                setSelectedSchedules(value);
                setShowEditScheduleView(false);
              }}
            />
          </Form.Item>
        )}

        {/* Show button to open or close EditScheduleView */}
        {/* {!showEditScheduleView ? (
          <Form.Item>
            <Button type="primary" block onClick={() => {
              setShowEditScheduleView(true);
              setSelectedSchedules([]);
            }}>
              Create New Schedule
            </Button>
          </Form.Item>
        ) : (
          <Form.Item>
            <Button type="primary" block onClick={() => {
              setShowEditScheduleView(false);
              setSelectedSchedules([]);
            }}>
              Cancel
            </Button>
          </Form.Item>
        )} */}

        {/* Show EditScheduleView only when showEditScheduleView is true */}
        {showEditScheduleView && (
          <EditScheduleView
            modelData={scheduleModelData}
            items={schedulesAsArray}
            editing={new Schedule({
              operatingDays: [true, true, true, true, true, false, false], // Default to weekdays
              schedulePeriods: [{
                start: dateRange[0],
                end: dateRange[1]
              }]
            })}
            handleItemUpdate={(item) => {
              handleCreateTimetable(item);
              setShowEditScheduleView(false);
              setSelectedSchedules([]);
            }}
            handleCancel={() => {
              setShowEditScheduleView(false);
              setSelectedSchedules([]);
            }}
            createItemFn={item => new Schedule(item)}
            validateFn={schedule => (schedule.includedSchedules?.length || schedule.schedulePeriods?.length) && Object.keys(schedule.operatingDays).some(day => schedule.operatingDays[day])}
          />
        )}

        {/* If a schedule (not 'new') is selected, show the Create Timetable button */}
        {!showEditScheduleView && selectedSchedules.length > 0 && !selectedSchedules.includes('new') && (
          <Form.Item>
            <Button type="primary" htmlType="submit" block
              onClick={() => handleCreateTimetable(selectedSchedules)}
            >
              Create Timetable
            </Button>
          </Form.Item>
        )}
      </Form>
    </div>
  );

  console.log('Final chart data:', chartData);
  console.log('Final trip dates:', tripDates);

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Space direction="horizontal" size="middle">
              <Space>
                <Switch 
                  checked={showStopTimes}
                  onChange={setShowStopTimes}
                />
                <Text>Show Actual Stop Times</Text>
              </Space>
              <Space>
                <Switch 
                  checked={showScheduledTime}
                  onChange={setShowScheduledTime}
                />
                <Text>Show Scheduled Time</Text>
              </Space>
              <Space>
                <Switch 
                  checked={showProposedTime}
                  onChange={setShowProposedTime}
                />
                <Text>Show Best OTR Time</Text>
              </Space>
              {/* <Space>
                <Switch 
                  checked={showModelLines}
                  onChange={setShowModelLines}
                />
                <Text>Show Model Lines</Text>
              </Space> */}
            </Space>
          </Col>
        </Row>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Alert
              message={
                <span>
                  <b>Current Scheduled OTR:</b> {avgScheduledOTR !== null ? avgScheduledOTR.toFixed(1) + '%' : 'N/A'}
                </span>
              }
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
            />
          </Col>
          <Col span={12}>
            <Alert
              message={
                <span>
                  <b>Proposed OTR (if operator used this timetable):</b> {avgProposedOTR !== null ? avgProposedOTR.toFixed(1) + '%' : 'N/A'}
                  <Popover
                    content={schedulePopoverContent}
                    title="Create new Timetable"
                    trigger="click"
                    placement="bottomRight"
                  >
                    <Button 
                      type="link" 
                      icon={<ScheduleOutlined />}
                      style={{ marginLeft: 8 }}
                    >
                      Create new Timetable
                    </Button>
                  </Popover>
                </span>
              }
              type="success"
              showIcon
              style={{ marginBottom: 8 }}
            />
          </Col>
        </Row>
      </div>
      {selectedRoute === 'all' || selectedTrip === 'all' ? (
        <Alert
          message="Select a Route and Trip"
          description={
            <Paragraph>
              Please select a specific route and trip from the filters above to view the service analysis.
              This will show you both the actual stop times and the proposed best OTR times.
            </Paragraph>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : !chartData || chartData.length === 0 ? (
        <Empty description="No data available for the selected route and trip" />
      ) : (
        <Card>
          <div style={{ height: 800 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="stopName"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  allowDataOverflow={true}
                  tick={(props) => (
                    <CustomXAxisTick
                      {...props}
                      isTimingPoint={props.payload.isTimingPoint}
                    />
                  )}
                />
                <YAxis
                  tickFormatter={value => {
                    const hours = Math.floor(value / (60 * 60 * 1000));
                    const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  }}
                  scale="time"
                  type="number"
                  allowDataOverflow={true}
                  domain={['dataMin', 'dataMax']}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                  labelFormatter={label => `Stop: ${label}`}
                  filterNull={true}
                  offset={100}
                />
                <Legend />
                {/* Scheduled Time Line */}
                {showScheduledTime && (
                  <Line
                    type="monotone"
                    dataKey="scheduledTime"
                    name="Scheduled Time"
                    stroke="#8884d8"
                    strokeWidth={3}
                    dot={true}
                  />
                )}
                {/* Proposed Time Line */}
                {showProposedTime && (
                  <Line
                    type="monotone"
                    dataKey="proposedTime"
                    name="Best OTR"
                    stroke="#52c41a"
                    strokeWidth={3}
                    dot={true}
                  />
                )}
                {/* Actual Stop Time Lines */}
                {showStopTimes && tripDates.map((tripDate, idx) => (
                  <Line
                    key={`actual-${tripDate}`}
                    type="monotone"
                    dataKey={`actual_${tripDate}`}
                    name={`Actual (${tripDate})`}
                    stroke="#cccccc"
                    strokeWidth={1}
                    strokeOpacity={0.5}
                    dot={true}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: 16 }}>
            <Alert
              message="Proposed OTR Service"
              description={
                <Paragraph>
                  This chart shows actual stop times (gray lines), the scheduled time (blue), and the proposed time (green) that would maximize on-time performance in the [-30, +120] second window.
                </Paragraph>
              }
              type="info"
              showIcon
            />
          </div>
        </Card>
      )}
    </>
  );
};

export default ProposedOTR; 